import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, CreditCard, X, Sparkles, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSubscription, PLAN_LABELS, PLAN_PRICING } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type ReminderLevel = 'info' | 'medium' | 'strong';

/**
 * Smart trial reminder popup system
 * - Shows every 4 hours
 * - Max 3 times per day
 * - Urgency increases as trial nears end
 */
export function TrialReminderPopup() {
  const navigate = useNavigate();
  const { subscription, isLoading } = useSubscription();
  const { tenant } = useAuth();
  const [open, setOpen] = useState(false);
  const [reminderLevel, setReminderLevel] = useState<ReminderLevel>('info');
  const [canShow, setCanShow] = useState(false);

  // Determine reminder level based on days remaining
  const getReminderLevel = useCallback((daysRemaining: number): ReminderLevel => {
    if (daysRemaining <= 5) return 'strong';
    if (daysRemaining <= 15) return 'medium';
    return 'info';
  }, []);

  // Check if we can show reminder (4 hour gap, max 3/day)
  const checkCanShowReminder = useCallback(async () => {
    if (!tenant?.id) return false;
    
    try {
      const { data, error } = await supabase.rpc('can_show_trial_reminder', {
        p_tenant_id: tenant.id
      });
      
      if (error) {
        console.error('Error checking reminder:', error);
        return false;
      }
      
      return data === true;
    } catch (err) {
      console.error('Error checking reminder:', err);
      return false;
    }
  }, [tenant?.id]);

  // Record that we showed a reminder
  const recordReminderShown = useCallback(async (level: ReminderLevel) => {
    if (!tenant?.id) return;
    
    try {
      await supabase.rpc('record_trial_reminder', {
        p_tenant_id: tenant.id,
        p_reminder_type: level
      });
    } catch (err) {
      console.error('Error recording reminder:', err);
    }
  }, [tenant?.id]);

  // Check and show reminder
  useEffect(() => {
    if (isLoading || !subscription || !tenant?.id) return;
    
    const { status, daysRemaining } = subscription;
    
    // Only show for trial status (not trial_pending_mandate)
    if (status !== 'trial') return;

    const checkAndShow = async () => {
      const canShowNow = await checkCanShowReminder();
      setCanShow(canShowNow);
      
      if (canShowNow) {
        const level = getReminderLevel(daysRemaining);
        setReminderLevel(level);
        
        // Small delay before showing
        setTimeout(() => {
          setOpen(true);
          recordReminderShown(level);
        }, 3000); // Show after 3 seconds of being on page
      }
    };

    // Check after a short delay to avoid showing immediately on page load
    const timer = setTimeout(checkAndShow, 5000);
    
    return () => clearTimeout(timer);
  }, [isLoading, subscription, tenant?.id, checkCanShowReminder, getReminderLevel, recordReminderShown]);

  const handleDismiss = () => {
    setOpen(false);
  };

  const handleViewPlans = () => {
    setOpen(false);
    navigate('/business/subscription');
  };

  if (!subscription) return null;

  const { daysRemaining, selectedPlanForTrial } = subscription;
  const planLabel = PLAN_LABELS[selectedPlanForTrial] || 'Yearly';
  const planPrice = PLAN_PRICING[selectedPlanForTrial] || 1299;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getReminderContent = () => {
    switch (reminderLevel) {
      case 'strong':
        return {
          title: 'Trial Ending Soon!',
          description: `Only ${daysRemaining} days left! Your ${planLabel} subscription (${formatCurrency(planPrice)}) will activate automatically.`,
          icon: <Clock className="h-6 w-6 text-destructive" />,
          iconBg: 'bg-destructive/10',
          showUrgent: true,
        };
      case 'medium':
        return {
          title: 'Trial Mode Active',
          description: `${daysRemaining} days remaining. Your ${planLabel} plan is ready to start after trial.`,
          icon: <Calendar className="h-6 w-6 text-orange-600" />,
          iconBg: 'bg-orange-500/10',
          showUrgent: false,
        };
      default:
        return {
          title: 'Welcome to Your Trial!',
          description: `You have ${daysRemaining} days to explore all features. Your ${planLabel} subscription will begin automatically.`,
          icon: <Sparkles className="h-6 w-6 text-amber-600" />,
          iconBg: 'bg-amber-500/10',
          showUrgent: false,
        };
    }
  };

  const content = getReminderContent();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`h-12 w-12 rounded-full ${content.iconBg} flex items-center justify-center`}>
              {content.icon}
            </div>
            <div>
              <DialogTitle className="flex items-center gap-2">
                {content.title}
                {content.showUrgent && (
                  <Badge variant="destructive" className="text-xs">
                    {daysRemaining} days left
                  </Badge>
                )}
              </DialogTitle>
              <DialogDescription className="mt-1">
                {content.description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Selected Plan</span>
              <span className="font-medium">{planLabel}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Amount after trial</span>
              <span className="font-medium text-primary">{formatCurrency(planPrice)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Auto-debit</span>
              <Badge variant="secondary" className="bg-green-500/10 text-green-600">
                Active
              </Badge>
            </div>
          </div>
          
          <p className="text-xs text-muted-foreground mt-3 text-center">
            You can change your plan or cancel anytime during the trial period.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={handleDismiss} className="gap-2">
            <X className="h-4 w-4" />
            Dismiss
          </Button>
          <Button onClick={handleViewPlans} className="gap-2">
            <CreditCard className="h-4 w-4" />
            View Plans
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
